import { useState, useMemo } from "react";
import { DateRange } from "react-day-picker";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { CaretDown, CheckCircle } from "@phosphor-icons/react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandList, CommandGroup, CommandItem } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { PageHeader } from "@/components/layout/heading";
import { ChannelGapLeader } from "@/components/brand-governance/performance-benchmark/channel-gap-leader";
import { CreativeVelocity } from "@/components/brand-governance/performance-benchmark/creative-velocity";
import { ShareOfVoice } from "@/components/brand-governance/performance-benchmark/share-of-voice";
import { VideoProduction } from "@/components/brand-governance/performance-benchmark/video-production";
import { Stats } from "@/components/brand-governance/performance-benchmark/stats";

import { useSession } from "@/hooks/use-session";
import { cn, getDataRangePreset } from "@/lib/utils";
import type { CompetitorBenchmarkParams } from "@/apis/brand-os/brand-governance";
import { mDateRanges, type dateRangeType } from "@/mock/brand-governance";

export const Route = createFileRoute("/dashboard/governance/_layout/performance-benchmark")({
  component: PerformanceBenchmark,
});

function buildCompetitorBenchmarkParams(brandName: string, rangeOption: dateRangeType, startDate: Date, endDate: Date | undefined): CompetitorBenchmarkParams {
  const start_date = format(startDate, "yyyy-MM-dd");
  const end_date = format(endDate ?? startDate, "yyyy-MM-dd");
  return {
    brand_name: brandName,
    period: rangeOption,
    ...(rangeOption === "custom" ? { start_date, end_date } : {}),
  };
}

export function PerformanceBenchmark() {
  const session = useSession();
  const brandName = session.profile.company?.company_name ?? "";

  const initialPreset = getDataRangePreset("MTD");
  const [rangeOption, setRangeOption] = useState<dateRangeType>("MTD");
  const [startDate, setStartDate] = useState<Date>(() => initialPreset.startDate);
  const [endDate, setEndDate] = useState<Date | undefined>(() => initialPreset.endDate);

  const params = useMemo(() => buildCompetitorBenchmarkParams(brandName, rangeOption, startDate, endDate), [brandName, rangeOption, startDate, endDate]);

  return (
    <div className="space-y-6">
      <PageHeader title="Performance Benchmark" />
      <DateFilters rangeOption={rangeOption} setRangeOption={setRangeOption} startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate} />
      <Stats params={params} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CreativeVelocity params={params} />
        <VideoProduction params={params} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ShareOfVoice params={{ highlightBrand: brandName }} />
        <ChannelGapLeader params={params} />
      </div>
    </div>
  );
}

type DateFiltersProps = {
  rangeOption: dateRangeType;
  setRangeOption: React.Dispatch<React.SetStateAction<dateRangeType>>;
  startDate: Date;
  setStartDate: React.Dispatch<React.SetStateAction<Date>>;
  endDate: Date | undefined;
  setEndDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
};

function DateFilters({ rangeOption, setRangeOption, startDate, setStartDate, endDate, setEndDate }: DateFiltersProps) {
  const [presetPopoverOpen, setPresetPopoverOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const onChangePresetPopoverOpen = (open: boolean) => {
    if (open) setCalendarOpen(false);
    setPresetPopoverOpen(open);
  };

  const onSelectPreset = (value: dateRangeType) => {
    setRangeOption(value);
    if (value === "custom") {
      setCalendarOpen(true);
      return;
    }
    const { startDate: nextStart, endDate: nextEnd } = getDataRangePreset(value);
    setStartDate(nextStart);
    setEndDate(nextEnd);
    onChangePresetPopoverOpen(false);
  };

  const onSelectDateRange = (range?: DateRange) => {
    if (!range?.from) return;
    if (!range.to || range.from.getTime() === range.to.getTime()) {
      setStartDate(range.from);
      setEndDate(range.from);
      return;
    }
    setStartDate(range.from);
    if (range.to) setEndDate(range.to);
  };

  return (
    <div className="flex flex-wrap items-center gap-3 p-2">
      <Popover open={presetPopoverOpen} onOpenChange={onChangePresetPopoverOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={presetPopoverOpen} className="h-9 min-w-60 justify-between rounded-lg border-input bg-card px-3">
            <span className="truncate">
              {rangeOption === "custom"
                ? endDate
                  ? `${format(startDate, "dd MMM yyyy")} - ${format(endDate, "dd MMM yyyy")}`
                  : "Custom"
                : mDateRanges.find((o) => o.value === rangeOption)?.label ?? rangeOption}
            </span>
            <CaretDown className="size-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-60 rounded-lg p-0" align="start">
          {!calendarOpen ? (
            <Command>
              <CommandList>
                <CommandGroup>
                  {mDateRanges.map((opt) => (
                    <CommandItem key={opt.value} value={opt.value} onSelect={() => onSelectPreset(opt.value)} className="justify-between">
                      {opt.label}
                      <CheckCircle weight="fill" className={cn("size-4.5 text-foreground", rangeOption === opt.value ? "opacity-100" : "opacity-0")} />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          ) : (
            <Calendar
              mode="range"
              selected={{ from: startDate, to: endDate }}
              classNames={{ head_row: "flex mb-1", row: "flex w-full mt-1.5" }}
              onSelect={onSelectDateRange}
              onDayClick={(day, modifiers) => {
                if (modifiers.selected && day.getTime() === startDate.getTime()) {
                  setStartDate(day);
                  setEndDate(day);
                }
              }}
              defaultMonth={startDate}
              initialFocus
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
